
import React from 'react';
import { useTeacherAttendance } from '../../hooks/useTeacherAttendance';
import { useAttendanceStore } from '../../store/attendanceStore';
import { useTimetableStore } from '../../store/timetableStore';
import { format } from 'date-fns';
import { Users, UserCheck, UserX, Clock } from 'lucide-react';

interface AttendanceStatsProps {
  className?: string;
}

const AttendanceStats: React.FC<AttendanceStatsProps> = ({ className = '' }) => {
  const { selectedDate } = useTeacherAttendance();
  const { attendanceRecords } = useAttendanceStore();
  const { timetableData } = useTimetableStore();
  
  // Get today's attendance data
  const todayStr = format(selectedDate, 'yyyy-MM-dd');
  const todayAttendance = attendanceRecords.filter(record => 
    record.date.startsWith(todayStr)
  );
  
  // Calculate stats from real data
  const totalTeachers = timetableData?.length || 0;
  const presentCount = todayAttendance.filter(record => record.status === 'present').length;
  const absentCount = todayAttendance.filter(record => record.status === 'absent').length;
  const lateCount = todayAttendance.filter(record => record.status === 'late').length;
  
  const attendanceRate = totalTeachers > 0 ? Math.round((presentCount / totalTeachers) * 100) : 0;
  
  const stats = [
    {
      title: 'Total Teachers',
      value: totalTeachers,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Present',
      value: presentCount,
      icon: UserCheck,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Absent',
      value: absentCount,
      icon: UserX,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      title: 'Late',
      value: lateCount,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
  ];

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <div key={stat.title} className={`${stat.bgColor} p-4 rounded-lg border`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                {stat.title === 'Present' && totalTeachers > 0 && (
                  <p className="text-xs text-gray-500">
                    {attendanceRate}% attendance rate
                  </p>
                )}
              </div>
              <Icon className={`h-8 w-8 ${stat.color}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AttendanceStats;
